import { useState, useEffect } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useContent } from "@/context/ContentContext";
import { useT } from "@/lib/translations";
import { useLocalize } from "@/lib/localize";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Heart, 
  Home, 
  Users, 
  Shield,
  Dog, 
  ChevronRight,
  PawPrint,
  Sparkles,
  HandHeart,
  Calendar,
  Instagram,
  Utensils,
  Stethoscope
} from "lucide-react";
import NextEventBanner from "@/components/NextEventBanner";
import PublicNav from "@/components/PublicNav";
import DonationFlow from "@/components/DonationFlow";

export default function LandingPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [featuredDogs, setFeaturedDogs] = useState([]);
  const { t } = useContent();
  const L = useT();  // Locale-aware helper for hardcoded strings
  const Lo = useLocalize();
  const { locale } = useLanguage();

  // Quick general support modal state
  const [quickSupport, setQuickSupport] = useState(null); // null | { type, presetAmount, presetDonationType, presetTitle }
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Deep-link handler — e.g. /?support=meal&amount=500 (from follow-up emails).
  // Opens the quick donation modal. `amount` is honored only if user explicitly
  // passed one (email CTAs, campaign links). Otherwise the modal opens on step 1
  // with a free-text input so the donor picks their own amount — no preset
  // commitments are ever shown platform-wide.
  useEffect(() => {
    const support = searchParams.get("support");
    const amount = parseInt(searchParams.get("amount") || "", 10);
    if (!support) return;
    const explicitAmount = Number.isFinite(amount) && amount > 0 ? amount : undefined;
    if (support === "meal") {
      setQuickSupport({
        type: "genel",
        presetAmount: explicitAmount,
        presetDonationType: "meal",
        presetTitle: "Mama Desteği",
      });
    } else if (support === "vet") {
      setQuickSupport({
        type: "genel",
        presetAmount: explicitAmount,
        presetDonationType: "vet_visit",
        presetTitle: "Veteriner Desteği",
      });
    } else if (support === "monthly") {
      setQuickSupport({
        type: "genel",
        presetAmount: explicitAmount,
        presetDonationType: "monthly_general",
        presetTitle: "Aylık Destek",
        monthlyMode: true,
      });
    }
    // Strip the params so refresh doesn't re-open the modal.
    const next = new URLSearchParams(searchParams);
    next.delete("support");
    next.delete("amount");
    setSearchParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchStats();
    fetchFeaturedDogs();
  }, []);

  // Hash-scroll handler — e.g. arriving from another page at /#hakkimizda,
  // or clicking Hakkımızda while already on the landing page.
  // Retries a few times because late-loading images (About hero, featured dogs)
  // can push the target section down after the first scroll fires.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const scrollIfNeeded = () => {
      const el = document.getElementById(id);
      if (!el) return;
      // If the section top is already within a small threshold of the sticky nav (~80px),
      // we consider the user "at" the section — don't yank them again.
      const top = el.getBoundingClientRect().top;
      if (Math.abs(top) < 90) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const timers = [100, 600, 1500, 3000].map((ms) => setTimeout(scrollIfNeeded, ms));
    return () => timers.forEach(clearTimeout);
  }, [location.hash]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Stats error:", error);
    }
  };

  const fetchFeaturedDogs = async () => {
    try {
      // Fetch a larger pool of priority dogs, then trim on-screen to the
      // largest multiple of 4. This keeps the grid symmetrical at every
      // breakpoint (1 / 2 / 4 columns) — no orphan card on Ana Sayfa.
      const response = await axios.get(`${API}/dogs?status=sahiplendirmeye_hazir&limit=12`);
      const dogs = response.data.dogs || [];
      // Largest multiple of 4 that fits the available dogs.
      const trimmed = dogs.length >= 4 ? dogs.slice(0, Math.floor(dogs.length / 4) * 4) : dogs;
      setFeaturedDogs(trimmed);
    } catch (error) {
      console.error("Dogs error:", error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <PublicNav />

      {/* Next Event Banner (shows only if there's an upcoming event) */}
      <NextEventBanner />

      {/* Hero Section */}
      <section className="relative hero-gradient overflow-hidden noise-overlay">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">{L("Pativista ile Birlikte")}</span>
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl tracking-tight leading-[0.95] md:leading-[0.9] text-foreground">
                {t("landing.hero.title_prefix", "Görünmez Canları")} <span className="text-primary">{t("landing.hero.title_highlight", "Görünür")}</span> {t("landing.hero.title_suffix", "Kılıyoruz")}
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-lg">
                {t("landing.hero.description", "Görünmez kalmasınlar: bir canı bugün görünür yap—hikâyesini aç, destek ağını büyüt.")}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/canlar">
                  <Button size="xl" className="btn-pill btn-primary bg-primary text-primary-foreground shadow-lg shadow-primary/20 transition-all duration-200 hover:bg-primary hover:text-white hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30">
                    {t("landing.hero.cta_primary", "Bir Canı Görünür Yap")}
                    <ChevronRight className="ml-1 w-5 h-5" />
                  </Button>
                </Link>
                <a href="#nasil-calisir">
                  <Button size="xl" variant="outline" className="btn-pill border-2 border-primary text-primary bg-white font-semibold transition-all duration-200 hover:bg-accent hover:border-accent hover:text-accent-foreground hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30">
                    {t("landing.hero.cta_secondary", "Nasıl Çalışır?")}
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative animate-fade-in-up stagger-2">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-primary/20">
                <img 
                  src="https://images.unsplash.com/photo-1526520112421-5054395689a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHN0cmF5JTIwZG9nJTIwcG9ydHJhaXQlMjBjbG9zZSUyMHVwJTIwd2FybSUyMGxpZ2h0aW5nfGVufDB8fHx8MTc2NjUyNjU2N3ww&ixlib=rb-4.1.0&q=85"
                  alt={L("Mutlu köpek")}
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="font-heading text-2xl text-white">
                    {L('"Dokunduğumuz her can; kimliği, hikayesi ve ağı olan sahiplenilebilir bir karakter olarak görünür hale geliyor."')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section — Impact Story */}
      {stats && (
        <section className="py-16 md:py-20 bg-card border-y border-border/50">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            {/* Hero stat: Total dogs */}
            <div className="text-center mb-12">
              <p className="font-heading text-6xl md:text-7xl text-primary font-semibold leading-none">
                {stats.total_dogs}
              </p>
              <p className="text-muted-foreground mt-3 text-base md:text-lg">
                {L("KAHU'da görünür kıldığımız can 🐾")}
              </p>
            </div>

            {/* Two relationship cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Card 1: Adopted progress */}
              <div className="bg-background/60 rounded-2xl p-6 md:p-8 border border-border/60 flex flex-col">
                <div className="flex items-baseline justify-between mb-2">
                  <p className="font-heading text-3xl md:text-4xl font-semibold text-foreground">
                    {stats.adopted_dogs}
                    <span className="text-muted-foreground text-xl md:text-2xl"> / {stats.total_dogs}</span>
                  </p>
                  <p className="text-sm font-medium text-primary">
                    {stats.total_dogs > 0 ? Math.round((stats.adopted_dogs / stats.total_dogs) * 100) : 0}%
                  </p>
                </div>
                <p className="text-foreground/80 mb-4">{L("Yuvasına Kavuştu")}</p>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-700"
                    style={{
                      width: `${stats.total_dogs > 0 ? (stats.adopted_dogs / stats.total_dogs) * 100 : 0}%`
                    }}
                  />
                </div>
                {/* CTA: I want to adopt */}
                <div className="mt-6 space-y-2">
                  <Link to="/canlar" data-testid="stats-adopt-cta">
                    <Button className="btn-pill btn-primary bg-primary text-primary-foreground w-full h-11 shadow-md shadow-primary/20 font-semibold transition-all duration-200 hover:bg-primary hover:text-white hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30">
                      <Home className="w-4 h-4 mr-2" />
                      {L("Ben de Sahiplenmek İstiyorum")}
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    onClick={() => setQuickSupport({
                      type: "genel",
                      monthlyMode: true,
                      presetTitle: L("Aylık Destek"),
                    })}
                    data-testid="stats-monthly-support-cta"
                    className="btn-pill w-full h-11 border-2 border-primary/40 bg-white text-primary font-semibold transition-all duration-200 hover:bg-accent hover:border-accent hover:text-accent-foreground hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30"
                  >
                    <HandHeart className="w-4 h-4 mr-2" />
                    {L("Sahiplenemiyorum, Destek Olayım")}
                  </Button>
                </div>
              </div>

              {/* Card 2: Safe ratio */}
              <div className="bg-background/60 rounded-2xl p-6 md:p-8 border border-border/60 flex flex-col">
                <div className="flex items-baseline justify-between mb-2">
                  <p className="font-heading text-3xl md:text-4xl font-semibold text-foreground">
                    {stats.total_dogs - (stats.deceased_dogs || 0)}
                    <span className="text-muted-foreground text-xl md:text-2xl"> / {stats.total_dogs}</span>
                  </p>
                  <p className="text-sm font-medium text-accent">
                    {stats.total_dogs > 0
                      ? Math.round(((stats.total_dogs - (stats.deceased_dogs || 0)) / stats.total_dogs) * 100)
                      : 0}%
                  </p>
                </div>
                <p className="text-foreground/80 mb-4">{L("Güvende")}</p>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent rounded-full transition-all duration-700"
                    style={{
                      width: `${stats.total_dogs > 0 ? ((stats.total_dogs - (stats.deceased_dogs || 0)) / stats.total_dogs) * 100 : 0}%`
                    }}
                  />
                </div>
                {(stats.deceased_dogs || 0) > 0 && (
                  <Link to="/seffaflik" className="block mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors">
                    · {stats.deceased_dogs} {L("can aramızdan ayrıldı — anıyoruz")}
                  </Link>
                )}
                {/* Quick-support CTAs */}
                <div className="mt-6">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-medium">
                    {L("Anında Destek")}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setQuickSupport({
                        type: "mama",
                        presetDonationType: "meal",
                        presetTitle: L("Genel Mama Desteği"),
                      })}
                      data-testid="stats-quick-meal"
                      className="btn-pill w-full min-w-0 h-12 px-3 text-sm border-2 border-secondary/60 bg-white text-secondary font-semibold transition-all duration-200 hover:bg-accent hover:border-accent hover:text-accent-foreground hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30"
                    >
                      <Utensils className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="text-center leading-tight">{L("Mama Desteği")}</span>
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setQuickSupport({
                        type: "vet",
                        presetDonationType: "vet_visit",
                        presetTitle: L("Genel Veteriner Desteği"),
                      })}
                      data-testid="stats-quick-vet"
                      className="btn-pill w-full min-w-0 h-12 px-3 text-sm border-2 border-secondary/60 bg-white text-secondary font-semibold transition-all duration-200 hover:bg-accent hover:border-accent hover:text-accent-foreground hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30"
                    >
                      <Stethoscope className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="text-center leading-tight">{locale === "en" ? "Vet Support" : "Veteriner"}</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* How It Works - 4 Pillars */}
      <section id="nasil-calisir" className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="font-heading text-4xl md:text-5xl tracking-tight text-foreground mb-4">
              {t("landing.howitworks.title", "Dijital Can Profili ve Sahiplendirme Motoru")}
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t("landing.howitworks.description", "WhatsApp karmaşasını bitir; her canı tek profilde topla, sahiplendirmeyi düzene sok.")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="card-hover bg-card border border-border/50 shadow-xl shadow-black/5">
              <CardContent className="p-7 text-center">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 mx-auto">
                  <Shield className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <div className="text-sm font-semibold text-primary mb-2">01</div>
                <h3 className="font-heading text-xl mb-3">{t("landing.feature1.title", "Dijital Kimlik")}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t("landing.feature1.description", "Mikroçip/kulak küpesiyle tek kimlik oluştur; aynı canı herkes doğru tanısın.")}
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover bg-card border border-border/50 shadow-xl shadow-black/5">
              <CardContent className="p-7 text-center">
                <div className="w-16 h-16 rounded-2xl bg-secondary/10 flex items-center justify-center mb-4 mx-auto">
                  <Heart className="w-8 h-8 text-secondary" strokeWidth={1.5} />
                </div>
                <div className="text-sm font-semibold text-secondary mb-2">02</div>
                <h3 className="font-heading text-xl mb-3">{t("landing.feature2.title", "Can Profili")}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t("landing.feature2.description", "Fotoğrafını yükle, sağlık ve davranış notunu ekle; bu canı sahiplenilebilir hale getir.")}
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover bg-card border border-border/50 shadow-xl shadow-black/5">
              <CardContent className="p-7 text-center">
                <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-4 mx-auto">
                  <Home className="w-8 h-8 text-accent" strokeWidth={1.5} />
                </div>
                <div className="text-sm font-semibold text-accent mb-2">03</div>
                <h3 className="font-heading text-xl mb-3">{t("landing.feature3.title", "Sahiplendirme Motoru")}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t("landing.feature3.description", "Başvuruları tek yerde topla, bilgilendirmeyi otomatikleştir; süreci kayıt altına al, yükünü hafiflet.")}
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover bg-card border border-border/50 shadow-xl shadow-black/5">
              <CardContent className="p-7 text-center">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 mx-auto">
                  <Users className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <div className="text-sm font-semibold text-primary mb-2">04</div>
                <h3 className="font-heading text-xl mb-3">{t("landing.feature4.title", "Yaşam Kanıtı")}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t("landing.feature4.description", "Say, kaydet, paylaş; her canın durumu şeffafça görünür olsun.")}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Dogs */}
      {featuredDogs.length > 0 && (
        <section className="py-12 md:py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-col gap-5 mb-8 md:mb-12 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-3">
                  <Sparkles className="w-3.5 h-3.5" />
                  {t("landing.dogs.badge", "Yuvasını Bekliyor")}
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl tracking-tight text-foreground mb-3 leading-tight">
                  {t("landing.dogs.title", "Seni Bekleyen Canlar")}
                </h2>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed">{t("landing.dogs.description", "Hikâyesini şimdi keşfet; o yeni başlangıcı sen başlat.")}</p>
              </div>
              <Link to="/canlar" className="sm:flex-shrink-0">
                <Button size="lg" variant="outline" className="btn-pill border-2 border-primary text-primary bg-white font-semibold transition-all duration-200 hover:bg-accent hover:border-accent hover:text-accent-foreground hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30 w-full sm:w-auto">
                  {t("landing.dogs.cta", "Şimdi Tanış")}
                  <ChevronRight className="ml-1 w-5 h-5" />
                </Button>
              </Link>
            </div>

            {/* Mobile: horizontal snap carousel; sm+: standard grid. */}
            <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 -mx-4 px-4 pb-4 scrollbar-hide sm:grid sm:grid-cols-2 sm:overflow-visible sm:snap-none sm:mx-0 sm:px-0 sm:pb-0 sm:gap-6 lg:grid-cols-4">
              {featuredDogs.map((dog) => (
                <Link
                  key={dog.dog_id}
                  to={`/can/${dog.dog_id}`}
                  className="snap-start shrink-0 w-[78%] sm:w-auto sm:shrink"
                >
                  <Card className="card-hover overflow-hidden bg-card border border-border/50 shadow-xl shadow-black/5 h-full">
                    <div className="img-zoom aspect-square">
                      <img
                        src={dog.photo_url || (dog.photos?.[0] ? `data:image/jpeg;base64,${dog.photos[0]}` : "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&h=400&fit=crop")}
                        alt={dog.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-heading text-xl">{dog.name}</h3>
                        <span className="text-sm font-medium px-3 py-1 rounded-full status-available">
                          {L("Sahiplenmeye Hazır")}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">
                        {Lo(dog, "breed") || L("Karışık")} · {dog.size === "small" ? L("Küçük") : dog.size === "medium" ? L("Orta") : dog.size === "large" ? L("Büyük") : L("Dev")}
                      </p>
                      <div className="flex gap-2 mt-4">
                        {dog.is_child_friendly && (
                          <span className="text-xs px-2.5 py-1 bg-muted rounded-full">Çocuk Dostu</span>
                        )}
                        {dog.is_apartment_suitable && (
                          <span className="text-xs px-2.5 py-1 bg-muted rounded-full">{L("Apartmana Uygun")}</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
              {/* Mobile-only end spacer so last card can center-snap */}
              <div className="shrink-0 w-2 sm:hidden" aria-hidden="true" />
            </div>
            {/* Mobile-only scroll hint */}
            <p className="text-xs text-muted-foreground text-center mt-2 sm:hidden">
              {L("← Kaydırarak keşfet →")}
            </p>
          </div>
        </section>
      )}

      {/* About Section */}
      <section id="hakkimizda" className="py-20 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="relative overflow-hidden sm:overflow-visible">
                <img
                  src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=800&h=600&fit=crop&q=85"
                  alt={L("Mutlu köpekler birlikte")}
                  className="rounded-3xl shadow-2xl"
                />
                <div className="absolute -bottom-3 -right-2 sm:-bottom-6 sm:-right-6 bg-primary text-primary-foreground px-4 py-3 sm:px-6 sm:py-4 rounded-2xl shadow-lg">
                  <p className="font-heading text-xl sm:text-2xl font-semibold">+{stats?.adopted_dogs || 0}</p>
                  <p className="text-xs sm:text-sm opacity-90">{L("Yuva bulan can")}</p>
                </div>
              </div>
              {/* KAHU isim hikâyesi — küçük, mütevazi, ana içerikten rol çalmaz */}
              <p className="mt-10 sm:mt-12 text-xs sm:text-sm text-muted-foreground leading-relaxed italic max-w-md">
                <span className="font-heading not-italic tracking-wide text-foreground/80">KAHU</span>
                {" — "}
                {L("kadim Hawaii kültüründe")}{" "}
                <span className="not-italic text-foreground/80">{L("koruyucu, kollayıcı, emanetçi")}</span>
                {L(" demektir. Buradan ilhamla: köpekler ve insanlar birbirinin sahibi değil, birbirinin ")}
                <span className="text-foreground/80">{locale === "en" ? "kahu" : "kahusu"}</span>
                {L(". Bu platform, o karşılıklı emanetin dijital karşılığı.")}
                <span className="ml-1 not-italic">🌺</span>
              </p>
            </div>
            
            <div className="space-y-6">
              <h2 className="font-heading text-4xl md:text-5xl tracking-tight text-foreground">
                {L("Pativista'dan")} <span className="text-primary">{L("Ölçeklenebilir")}</span> {L("Modele")}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {L("Pativista gibi yüksek standartlı yaşam alanları model üretecek kapasitede. KAHU ile onların sahada başardığını dijital ile güçlendirerek sürdürülebilir ve ölçeklenebilir bir çözüm yaratıyoruz.")}
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Shield className="w-6 h-6 text-primary mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="font-medium">{L("Hayvanlar için")}</p>
                    <p className="text-sm text-muted-foreground">{L("İsimsiz kalmak yerine, kimliği, hikâyesi ve ağı olan bireyler yaratıyoruz")}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Users className="w-6 h-6 text-primary mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="font-medium">{L("Gönüllüler için")}</p>
                    <p className="text-sm text-muted-foreground">{L("WhatsApp kaosundan çıkıp düzenli ve kayıtlı bir sisteme geçiyoruz")}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Heart className="w-6 h-6 text-primary mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="font-medium">{L("Toplum için")}</p>
                    <p className="text-sm text-muted-foreground">{L("Şeffaf, takip edilebilir ve güven yaratan bir yapı kuruyoruz")}</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary text-secondary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-primary/30 blur-3xl" />
          <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full bg-accent/30 blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center relative z-10">
          <h2 className="font-heading text-4xl md:text-5xl tracking-tight mb-6">
            {t("landing.cta.title", "Değişim Seninle Başlar")}
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            {t("landing.cta.description", "Bir adım at: sahiplen, koruyucu aile ol ya da destek ver—gel, bugün bir canın hayatını birlikte değiştirelim.")}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/canlar">
              <Button size="xl" className="btn-pill bg-primary text-primary-foreground shadow-lg font-semibold transition-all duration-200 hover:bg-primary hover:text-white hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30">
                {t("landing.cta.button", "Hemen Destek Ol")}
              </Button>
            </Link>
          </div>
            {stats?.adopted_dogs > 0 ? (
              <p className="mt-8 text-sm opacity-70">
                {`${stats.adopted_dogs} ${L("can yuva buldu")}`}
                {(stats.total_dogs - (stats.deceased_dogs || 0)) > 0 && ` · ${stats.total_dogs - (stats.deceased_dogs || 0)} ${L("can güvende")}`}
              </p>
            ) : null}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-card border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <PawPrint className="h-6 w-6 text-primary" strokeWidth={1.5} />
              <span className="font-heading text-xl">KAHU</span>
              <span className="font-heading text-xl text-foreground/80" data-testid="footer-tagline">
                {t("footer.tagline", "Şeffaflık Yaşatır!")}
              </span>
            </div>
            <div className="flex items-center gap-5">
              <a
                href="https://www.instagram.com/pativista/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Pativista Instagram"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <Link to="/gizlilik" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-privacy">
                {L("Gizlilik")}
              </Link>
              <Link to="/kvkk" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-kvkk">
                {L("KVKK")}
              </Link>
              <Link to="/iletisim" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-contact">
                {L("İletişim")}
              </Link>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("kahu:open-cookie-consent"))}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="footer-cookie-settings"
              >
                {L("Çerez Ayarları")}
              </button>
              <Link to="/admin" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                <Shield className="w-3 h-3" />
                {L("Yönetim")}
              </Link>
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-6 pt-6 border-t border-border/30">
            © {new Date().getFullYear()} {t("footer.copyright", L("KAHU - Tüm hakları saklıdır."))}
          </p>
        </div>
      </footer>

      {/* Quick general-support donation modal (triggered from stats CTAs) */}
      {quickSupport && (
        <DonationFlow
          open={!!quickSupport}
          onClose={() => setQuickSupport(null)}
          type={quickSupport.type}
          dogId={null}
          dogName={quickSupport.presetTitle}
          presetAmount={quickSupport.presetAmount}
          presetDonationType={quickSupport.presetDonationType}
          presetTitle={quickSupport.presetTitle}
          monthlyMode={quickSupport.monthlyMode}
        />
      )}
    </div>
  );
}
